import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, FormControl, Row, Table } from 'react-bootstrap';
import {
  ArrowUpRightCircle,
  CashStack,
  FileEarmarkSpreadsheet,
  GraphUpArrow,
  People,
  Printer,
  Search,
  X
} from 'react-bootstrap-icons';
import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../lead/leadReport.scss';
import { ABORT_ERROR, makeApiRequests } from '../../../helpers/api';
import {
  ENDPOINTS,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  expenseFields,
  incomeFields
} from '../../../helpers/constants';
import {
  calculateCommissionDue,
  calculateTotalGP,
  downloadFileFromBase64,
  formatInventoryUnit
} from '../../../helpers/global';
import { isAdmin, userHasAnyRole } from '../../../helpers/roleHelpers';
import { getProfileFromLocalStorage, getSalesRepOfSalesManager } from '../../../helpers/session';
import Loader from '../../Loader';
import AdvanceDateRangePicker from '../../common/AdvanceDateRangePicker';
import SearchableSelect from '../../common/SearchableSelect';
import PnLSidebar from '../../PnlSidebar';
import DochubSidebar from '../../pipeline/DochubSidebar';
import { useAdvancedDateRangePicker } from '../../../hooks/useAdvancedDateRangePicker';

const GROUP_BY = {
  SALES_MANAGER: 'salesManager',
  SALES_REP: 'salesRep',
  SALES_LOCATION: 'salesLocation',
  LENDER: 'lender'
};

/** Whole days from Date of Deposit to Doc Hub Date Funded; blank if either missing. */
const calcTimeToFundDays = (dateFunded, dateOfDeposit) => {
  if (!dateFunded || !dateOfDeposit) return null;
  const funded = moment(dateFunded).startOf('day');
  const deposit = moment(dateOfDeposit).startOf('day');
  if (!funded.isValid() || !deposit.isValid()) return null;
  return funded.diff(deposit, 'days');
};

const formatDateFunded = dateFunded => {
  if (!dateFunded) return '';
  const m = moment(dateFunded);
  return m.isValid() ? m.format('MMM D, YYYY') : '';
};

const SUMMARY_ACCENTS = {
  purple: { card: 'lead-report--accent-purple', icon: 'lead-report__icon-purple' },
  blue: { card: 'lead-report--accent-blue', icon: 'lead-report__icon-blue' },
  gold: { card: 'lead-report--accent-gold', icon: 'lead-report__icon-gold' },
  green: { card: 'lead-report--accent-purple', icon: 'lead-report__icon-blue' }
};

const EMPTY_SUMMARY = {
  assigned: 0,
  closed: 0,
  salesAmount: 0,
  commission: 0,
  conversionRate: '0.0'
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const formatCurrency = value => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const formatCurrencyDetail = value => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
};

const ClosedAtCell = ({ value }) => {
  if (!value) return '—';
  const m = moment(value);
  if (!m.isValid()) return '—';
  return (
    <span className="lead-report__generated-at">
      <span className="lead-report__generated-date">{m.format('YYYY-MM-DD')}</span>
      <span className="lead-report__generated-time">{m.format('hh:mm A')}</span>
    </span>
  );
};

const ToolbarFilter = ({ label, htmlFor, children, wide = false }) => (
  <div className={`lead-report__toolbar-filter${wide ? ' lead-report__toolbar-filter--search' : ''}`}>
    <label className="lead-report__toolbar-label" htmlFor={htmlFor}>
      {label}
    </label>
    {children}
  </div>
);

const FilterSelect = ({ id, value, options, optionValues, onChange, ariaLabel, isClearable = false }) => {
  const selectOptions = useMemo(
    () =>
      options.map((option, index) => ({
        label: option,
        value: optionValues ? optionValues[index] : option
      })),
    [options, optionValues]
  );

  return (
    <div className="lead-report__searchable-select-wrap">
      <SearchableSelect
        inputId={id}
        size="sm"
        options={selectOptions}
        value={value}
        onChange={nextValue => onChange(nextValue === '' || nextValue == null ? 'All' : nextValue)}
        placeholder={ariaLabel || 'Select...'}
        isClearable={isClearable}
        aria-label={ariaLabel || id}
        className="lead-report__searchable-select"
      />
    </div>
  );
};

const PRINT_SECTION = {
  CLOSED_DEALS: 'closedDeals',
  GROUPED_SUMMARY: 'groupedSummary'
};

const getConversionBadgeTone = conversionRate => {
  const value = Number(conversionRate);
  if (!Number.isFinite(value)) return 'neutral';
  if (value >= 75) return 'high';
  if (value >= 50) return 'mid-high';
  if (value < 25) return 'low';
  return 'mid';
};

const DashboardSummaryCard = ({ accent, icon: Icon, title, children }) => {
  const accentClass = SUMMARY_ACCENTS[accent];
  return (
    <Col xs={12} sm={6} lg={3}>
      <div className={`lead-report__summary-card ${accentClass.card}`}>
        <div className="lead-report__summary-header">
          <Icon size={18} className={`lead-report__summary-icon ${accentClass.icon}`} />
          <span>{title}</span>
        </div>
        <div className="lead-report__summary-body">{children}</div>
      </div>
    </Col>
  );
};

const SummaryCardSkeleton = ({ accent }) => {
  const accentClass = SUMMARY_ACCENTS[accent] || SUMMARY_ACCENTS.purple;
  return (
    <Col xs={12} sm={6} lg={3}>
      <div className={`lead-report__summary-card ${accentClass.card}`}>
        <div className="lead-report__summary-header">
          <span className="lead-report__summary-skeleton lead-report__summary-skeleton--icon" />
          <span className="lead-report__summary-skeleton lead-report__summary-skeleton--title" />
        </div>
        <div className="lead-report__summary-body">
          <div className="lead-report__summary-skeleton lead-report__summary-skeleton--value" />
        </div>
      </div>
    </Col>
  );
};

const ContractLinkCell = ({ contractId, buyer }) => {
  if (!contractId) return '—';
  return (
    <Link
      className="lead-report__exported-wrap"
      target="_blank"
      rel="noopener noreferrer"
      to={`/contract/${contractId}`}
      title={`Open contract: ${buyer || contractId}`}
    >
      <span className="lead-report__exported-label">{buyer || 'View contract'}</span>
      <ArrowUpRightCircle size={14} className="lead-report__exported-icon" aria-hidden />
    </Link>
  );
};

const SalesReport = ({ appChoices = [] }) => {
  const profile = getProfileFromLocalStorage();
  const adminUser = isAdmin();
  const salesManagerUser = userHasAnyRole(ROLE_SALES_MANAGER) && !adminUser;
  const salesRepUser =
    userHasAnyRole(ROLE_SALES, ROLE_SALES_AND_INVENTORY_MANAGER) && !userHasAnyRole(ROLE_SALES_MANAGER) && !adminUser;

  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [closedDealsSummary, setClosedDealsSummary] = useState(EMPTY_SUMMARY);
  const [groupsByType, setGroupsByType] = useState({
    [GROUP_BY.SALES_REP]: [],
    [GROUP_BY.SALES_MANAGER]: [],
    [GROUP_BY.SALES_LOCATION]: []
  });
  const [deals, setDeals] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    salesLocations: [],
    salesManagers: [],
    salesReps: [],
    lenders: []
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [printingSection, setPrintingSection] = useState(null);
  const [pnlDeal, setPnlDeal] = useState(null);
  const [showPnlSidebar, setShowPnlSidebar] = useState(false);
  const [openDochubMeta, setOpenDochubMeta] = useState(null);

  const [groupBy, setGroupBy] = useState(GROUP_BY.SALES_REP);
  const [groupLocationFilter, setGroupLocationFilter] = useState('All');
  const [salesLocationFilter, setSalesLocationFilter] = useState('All');
  const [salesManagerFilter, setSalesManagerFilter] = useState('All');
  const [contactOwnerFilter, setContactOwnerFilter] = useState('All');
  const [lenderFilter, setLenderFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const fetchAbortRef = useRef(null);
  const groupsFetchAbortRef = useRef(null);
  const lastSummaryDateKeyRef = useRef(null);

  const initialDateRange = useMemo(
    () => ({
      startDate: moment()
        .startOf('month')
        .toDate(),
      endDate: moment()
        .endOf('month')
        .toDate(),
      key: 'selection',
      rangeType: 'This Month'
    }),
    []
  );
  const { dateRange, setDateRange, handleNavigateDate } = useAdvancedDateRangePicker(initialDateRange);

  const dateRangeKey = useMemo(
    () =>
      `${
        dateRange.startDate
          ? moment(dateRange.startDate)
              .startOf('day')
              .valueOf()
          : ''
      }-${
        dateRange.endDate
          ? moment(dateRange.endDate)
              .endOf('day')
              .valueOf()
          : ''
      }`,
    [dateRange.startDate, dateRange.endDate]
  );

  const debouncedSetSearch = useRef(
    debounce(value => {
      setDebouncedSearchQuery(value);
    }, 300)
  ).current;

  useEffect(() => {
    debouncedSetSearch(searchQuery.trim());
    return () => debouncedSetSearch.cancel();
  }, [searchQuery, debouncedSetSearch]);

  const salesRepOptions = useMemo(() => {
    if (adminUser) {
      if (salesManagerFilter !== 'All') {
        const manager = filterOptions.salesManagers.find(m => m._id === salesManagerFilter);
        const reps = manager?.salesReps || [];
        const managerOption = manager ? [{ label: manager.name, value: manager._id }] : [];
        return [{ label: 'All', value: 'All' }, ...managerOption, ...reps.map(r => ({ label: r.name, value: r._id }))];
      }
      return [
        { label: 'All', value: 'All' },
        ...(filterOptions.salesReps || []).map(r => ({ label: r.name, value: r._id }))
      ];
    }

    if (salesManagerUser) {
      const localReps = getSalesRepOfSalesManager();
      const reps = filterOptions.salesReps?.length
        ? filterOptions.salesReps
        : [{ _id: profile._id, name: profile.name }, ...(localReps || [])];
      return [{ label: 'All', value: 'All' }, ...reps.map(r => ({ label: r.name, value: r._id }))];
    }

    return [];
  }, [adminUser, salesManagerUser, salesManagerFilter, filterOptions, profile]);

  const buildReportRequestBody = useCallback(
    () => ({
      datePreset: 'custom',
      startDate: dateRange.startDate
        ? moment(dateRange.startDate)
            .startOf('day')
            .toISOString()
        : undefined,
      endDate: dateRange.endDate
        ? moment(dateRange.endDate)
            .endOf('day')
            .toISOString()
        : undefined,
      salesLocationFilter,
      salesManagerFilter,
      contactOwnerFilter,
      lenderFilter,
      searchQuery: debouncedSearchQuery,
      page,
      limit
    }),
    [
      dateRange,
      salesLocationFilter,
      salesManagerFilter,
      contactOwnerFilter,
      lenderFilter,
      debouncedSearchQuery,
      page,
      limit
    ]
  );

  const printSalesReport = async section => {
    if (printingSection) return;

    setPrintingSection(section);
    toast.info(
      section === PRINT_SECTION.GROUPED_SUMMARY
        ? 'Generating grouped summary PDF, please wait...'
        : 'Generating closed deals PDF, please wait...'
    );

    const { page, limit, ...filters } = buildReportRequestBody();

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_PRINT_SALES_REPORT,
      requestBody: {
        ...filters,
        groupBy,
        groupLocationFilter,
        section
      }
    });

    setPrintingSection(null);

    if (error) {
      toast.error(error);
      return;
    }

    const { fileName, data, mimeType } = response || {};
    if (!data) {
      toast.error('Unable to generate sales report PDF.');
      return;
    }

    toast.success('Sales report PDF ready, downloading...');
    const downloaded = downloadFileFromBase64({ fileName, data, mimeType });
    if (!downloaded) {
      toast.error('Unable to download sales report PDF.');
    }
  };

  const fetchDealsSection = useCallback(async () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    const dateChanged = lastSummaryDateKeyRef.current !== dateRangeKey;
    if (dateChanged) {
      setSummaryLoading(true);
      lastSummaryDateKeyRef.current = dateRangeKey;
    }
    setDealsLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_SEARCH_SALES_REPORT,
      requestBody: buildReportRequestBody(),
      signal: controller.signal
    });

    if (error === ABORT_ERROR) return;

    setSummaryLoading(false);
    setDealsLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    setSummary(response?.summary || EMPTY_SUMMARY);
    setClosedDealsSummary(response?.closedDealsSummary || response?.summary || EMPTY_SUMMARY);
    setDeals(response?.deals || []);
    setFilterOptions(
      response?.filterOptions || { salesLocations: [], salesManagers: [], salesReps: [], lenders: [] }
    );
    setPagination(response?.pagination || { page: 1, limit, total: 0, totalPages: 1 });
  }, [buildReportRequestBody, dateRangeKey, limit]);

  const fetchGroupsSection = useCallback(async () => {
    if (groupsFetchAbortRef.current) {
      groupsFetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    groupsFetchAbortRef.current = controller;

    setGroupsLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_SEARCH_SALES_REPORT,
      requestBody: {
        datePreset: 'custom',
        startDate: dateRange.startDate
          ? moment(dateRange.startDate)
              .startOf('day')
              .toISOString()
          : undefined,
        endDate: dateRange.endDate
          ? moment(dateRange.endDate)
              .endOf('day')
              .toISOString()
          : undefined,
        groupLocationFilter,
        page: 1,
        limit: 1
      },
      signal: controller.signal
    });

    if (error === ABORT_ERROR) return;

    setGroupsLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    setGroupsByType(
      response?.groupsByType || {
        [GROUP_BY.SALES_REP]: response?.groups || [],
        [GROUP_BY.SALES_MANAGER]: [],
        [GROUP_BY.SALES_LOCATION]: [],
        [GROUP_BY.LENDER]: []
      }
    );
    setFilterOptions(
      response?.filterOptions || { salesLocations: [], salesManagers: [], salesReps: [], lenders: [] }
    );
  }, [dateRange, groupLocationFilter]);

  useEffect(() => {
    fetchDealsSection();
  }, [fetchDealsSection]);

  useEffect(() => {
    fetchGroupsSection();
  }, [fetchGroupsSection]);

  useEffect(() => {
    return () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      if (groupsFetchAbortRef.current) {
        groupsFetchAbortRef.current.abort();
      }
    };
  }, []);

  const setFilterAndResetPage = setter => value => {
    setPage(1);
    setter(value);
  };

  const resetAllFilters = () => {
    setPage(1);
    setSearchQuery('');
    setSalesLocationFilter('All');
    setSalesManagerFilter('All');
    setContactOwnerFilter('All');
    setLenderFilter('All');
  };

  const handleSalesManagerChange = value => {
    setPage(1);
    setSalesManagerFilter(value);
    setContactOwnerFilter('All');
  };

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (searchQuery.trim()) {
      chips.push({ key: 'search', label: `"${searchQuery.trim()}"`, onRemove: () => setSearchQuery('') });
    }
    if (salesLocationFilter !== 'All') {
      const loc = filterOptions.salesLocations.find(l => l._id === salesLocationFilter);
      chips.push({
        key: 'location',
        label: loc?.location || 'Location',
        onRemove: () => setSalesLocationFilter('All')
      });
    }
    if (salesManagerFilter !== 'All') {
      const mgr = filterOptions.salesManagers.find(m => m._id === salesManagerFilter);
      chips.push({
        key: 'manager',
        label: mgr?.name || 'Manager',
        onRemove: () => handleSalesManagerChange('All')
      });
    }
    if (contactOwnerFilter !== 'All') {
      const rep = salesRepOptions.find(o => o.value === contactOwnerFilter);
      chips.push({
        key: 'rep',
        label: rep?.label || 'Sales Rep',
        onRemove: () => setContactOwnerFilter('All')
      });
    }
    if (lenderFilter !== 'All') {
      const lender = (filterOptions.lenders || []).find(l => l._id === lenderFilter);
      chips.push({
        key: 'lender',
        label: lender?.name || lender?.lienholderCorporateName || lender?.shortName || 'Lender',
        onRemove: () => setLenderFilter('All')
      });
    }
    return chips;
  }, [
    searchQuery,
    salesLocationFilter,
    salesManagerFilter,
    contactOwnerFilter,
    lenderFilter,
    filterOptions,
    salesRepOptions
  ]);

  const groupTableLabel = useMemo(() => {
    if (groupBy === GROUP_BY.SALES_MANAGER) return 'Sales Manager';
    if (groupBy === GROUP_BY.SALES_LOCATION) return 'Sales Location';
    if (groupBy === GROUP_BY.LENDER) return 'Lender';
    return 'Assigned To';
  }, [groupBy]);

  const groups = useMemo(() => groupsByType[groupBy] || [], [groupsByType, groupBy]);

  const showGroupLocationFilter =
    !salesRepUser && groupBy !== GROUP_BY.SALES_LOCATION && (filterOptions.salesLocations || []).length > 0;

  const summaryCsvData = useMemo(
    () =>
      groups.map(g => ({
        [groupTableLabel]: g.label,
        Assigned: g.assigned,
        Closed: g.closed,
        'Sales Amount': g.salesAmount,
        Commission: g.commission,
        'Conversion %': g.conversionRate
      })),
    [groups, groupTableLabel]
  );

  const dealsCsvData = useMemo(
    () =>
      deals.map(d => {
        const timeToFundDays = calcTimeToFundDays(d.additionalFields?.dateFunded, d.dateOfDeposit);
        return {
          Closed: d.closedAt ? moment(d.closedAt).format('YYYY-MM-DD') : '',
          Buyer: d.buyer || '',
          Store: d.salesLocation?.location || '',
          'Assigned To': d.contactOwner?.name || '',
          Unit: formatInventoryUnit(d.inventoryUnit),
          'Sales Price': d.salesPrice,
          Commission: d.commissionDue,
          TotalGP: d.totalGP,
          'Date Funded': formatDateFunded(d.additionalFields?.dateFunded),
          'Time to Fund': timeToFundDays == null ? '' : timeToFundDays
        };
      }),
    [deals]
  );

  const openPnlSidebar = deal => {
    setPnlDeal(deal);
    setShowPnlSidebar(true);
  };

  const recalculateDealTotals = deal => {
    const updatedDeal = { ...deal, newStatus: 'Contracted' };
    return {
      ...updatedDeal,
      commissionDue: calculateCommissionDue(updatedDeal),
      // Same formula as P&L sidebar / backend pnlHelpers.calculateTotalGP
      totalGP: calculateTotalGP(updatedDeal, appChoices, expenseFields, incomeFields('Contracted'))
    };
  };

  const handlePnlUpdate = updatedInventory => {
    if (!pnlDeal?._id) return;

    setDeals(prev =>
      prev.map(deal => {
        if (deal._id !== pnlDeal._id) return deal;
        return recalculateDealTotals({ ...deal, inventoryUnit: updatedInventory });
      })
    );
    setPnlDeal(prev => (prev ? { ...prev, inventoryUnit: updatedInventory } : prev));
  };

  const handleCommissionSheetUpdate = (updatedResponse, isPrintOperation = false) => {
    if (!updatedResponse?._id) return;

    setDeals(prev =>
      prev.map(deal => {
        if (deal._id === updatedResponse._id) {
          return recalculateDealTotals({
            ...deal,
            inventoryUnit: updatedResponse.inventoryUnit || deal.inventoryUnit,
            salesPrice: updatedResponse.salesPrice ?? deal.salesPrice,
            additionalFields: updatedResponse.additionalFields || deal.additionalFields
          });
        }

        if (deal.inventoryUnit?._id === updatedResponse._id) {
          return recalculateDealTotals({ ...deal, inventoryUnit: updatedResponse });
        }

        return deal;
      })
    );

    if (!isPrintOperation) {
      setOpenDochubMeta(null);
    }
  };

  const showFilters = adminUser || salesManagerUser;

  return (
    <div className="lead-report">
      <div className="lead-report__page-header">
        <h1 className="lead-report__title">Sales Report</h1>
        <div className="lead-report__date-filter-row">
          <AdvanceDateRangePicker
            value={dateRange}
            onChange={ranges => {
              setPage(1);
              setDateRange(ranges.selection);
            }}
            showNavigator
            rangeType={dateRange.rangeType}
            handleNavigateDate={(rangeType, direction) => {
              setPage(1);
              handleNavigateDate(rangeType, direction);
            }}
          />
        </div>
      </div>

      <Row className="mb-2" style={{ rowGap: '12px' }}>
        {summaryLoading ? (
          <>
            <SummaryCardSkeleton accent="purple" />
            <SummaryCardSkeleton accent="blue" />
            <SummaryCardSkeleton accent="gold" />
            <SummaryCardSkeleton accent="green" />
          </>
        ) : (
          <>
            <DashboardSummaryCard accent="purple" icon={People} title="Deals Closed">
              <div className="lead-report__summary-value">{summary.closed}</div>
              <div className="lead-report__summary-detail">{summary.assigned} assigned in period</div>
            </DashboardSummaryCard>
            <DashboardSummaryCard accent="blue" icon={CashStack} title="Total Sales">
              <div className="lead-report__summary-value">{formatCurrency(summary.salesAmount)}</div>
              <div className="lead-report__summary-detail">Closed deal sales price</div>
            </DashboardSummaryCard>
            <DashboardSummaryCard accent="gold" icon={CashStack} title="Total Commission">
              <div className="lead-report__summary-value">{formatCurrency(summary.commission)}</div>
              <div className="lead-report__summary-detail">Commission due on closed deals</div>
            </DashboardSummaryCard>
            <DashboardSummaryCard accent="green" icon={GraphUpArrow} title="Conversion Rate">
              <div className="lead-report__summary-value">{summary.conversionRate}%</div>
              <div className="lead-report__summary-detail">
                {summary.closed} closed / {summary.assigned} assigned
              </div>
            </DashboardSummaryCard>
          </>
        )}
      </Row>

      {showFilters && (
        <div className="lead-report__toolbar mb-2">
          <div className="lead-report__toolbar-row">
            <ToolbarFilter label="Search" htmlFor="salesReportSearch" wide>
              <div className="lead-report__search-wrap">
                <Search className="lead-report__search-icon" size={16} aria-hidden />
                <FormControl
                  id="salesReportSearch"
                  className="lead-report__control lead-report__control--search"
                  placeholder="Buyer, store, assignee..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </ToolbarFilter>

            {adminUser && (
              <>
                <ToolbarFilter label="Sales Location" htmlFor="salesLocationFilter">
                  <FilterSelect
                    id="salesLocationFilter"
                    ariaLabel="Sales location"
                    value={salesLocationFilter}
                    options={['All', ...(filterOptions.salesLocations || []).map(l => l.location)]}
                    optionValues={['All', ...(filterOptions.salesLocations || []).map(l => l._id)]}
                    onChange={setFilterAndResetPage(setSalesLocationFilter)}
                  />
                </ToolbarFilter>

                <ToolbarFilter label="Sales Manager" htmlFor="salesManagerFilter">
                  <FilterSelect
                    id="salesManagerFilter"
                    ariaLabel="Sales manager"
                    value={salesManagerFilter}
                    options={['All', ...(filterOptions.salesManagers || []).map(m => m.name)]}
                    optionValues={['All', ...(filterOptions.salesManagers || []).map(m => m._id)]}
                    onChange={handleSalesManagerChange}
                  />
                </ToolbarFilter>
              </>
            )}

            {(adminUser || salesManagerUser) && (
              <ToolbarFilter label="Sales Rep" htmlFor="contactOwnerFilter">
                <FilterSelect
                  id="contactOwnerFilter"
                  ariaLabel="Sales rep"
                  value={contactOwnerFilter}
                  options={salesRepOptions.map(o => o.label)}
                  optionValues={salesRepOptions.map(o => o.value)}
                  onChange={setFilterAndResetPage(setContactOwnerFilter)}
                />
              </ToolbarFilter>
            )}

            {(adminUser || salesManagerUser) && (filterOptions.lenders || []).length > 0 && (
              <ToolbarFilter label="Lender" htmlFor="lenderFilter">
                <FilterSelect
                  id="lenderFilter"
                  ariaLabel="Lender"
                  value={lenderFilter}
                  options={[
                    'All',
                    ...(filterOptions.lenders || []).map(
                      l => l.name || l.lienholderCorporateName || l.shortName
                    )
                  ]}
                  optionValues={['All', ...(filterOptions.lenders || []).map(l => l._id)]}
                  onChange={setFilterAndResetPage(setLenderFilter)}
                />
              </ToolbarFilter>
            )}

            <button type="button" className="lead-report__reset-btn" onClick={resetAllFilters}>
              Reset deals filters
            </button>
          </div>

          {activeFilterChips.length > 0 && (
            <div className="lead-report__active-filters">
              <span className="lead-report__active-label">Active filters:</span>
              {activeFilterChips.map((chip, chipIndex) => (
                <button
                  key={`${chip.key}-${chipIndex}`}
                  type="button"
                  className="lead-report__filter-chip"
                  onClick={chip.onRemove}
                >
                  {chip.label}
                  <X size={12} aria-hidden />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="lead-report__table-card mb-3">
        <div
          className="d-flex align-items-center justify-content-between px-3 py-3 flex-wrap"
          style={{ gap: '0.75rem' }}
        >
          <h2 className="h6 mb-0">Closed Deals</h2>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem' }}>
            <button
              type="button"
              className="lead-report__reset-btn"
              onClick={() => printSalesReport(PRINT_SECTION.CLOSED_DEALS)}
              disabled={!!printingSection || dealsLoading || pagination.total === 0}
            >
              <Printer size={14} />
              {printingSection === PRINT_SECTION.CLOSED_DEALS ? 'Printing...' : 'Print'}
            </button>
            {!dealsLoading && dealsCsvData.length > 0 && (
              <CSVLink
                className="lead-report__reset-btn text-decoration-none"
                data={dealsCsvData}
                filename={`sales-report-deals-${moment().format('YYYY-MM-DD')}.csv`}
              >
                <FileEarmarkSpreadsheet size={14} className="text-success" />
                Export deals
              </CSVLink>
            )}
          </div>
        </div>

        <div className="px-3 py-3">
          <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.75rem 1.5rem', lineHeight: 1 }}>
            <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
              <People size={15} className="text-primary" style={{ flexShrink: 0 }} />
              <span className="text-muted smallFont mb-0">Deals Closed:</span>
              <strong style={{ color: '#000' }}>{closedDealsSummary.closed}</strong>
            </div>

            <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
              <People size={15} className="text-primary" style={{ flexShrink: 0 }} />
              <span className="text-muted smallFont mb-0">Total Assigned:</span>
              <strong style={{ color: '#000' }}>{closedDealsSummary.assigned}</strong>
            </div>

            <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
              <CashStack size={15} className="text-primary" style={{ flexShrink: 0 }} />
              <span className="text-muted smallFont mb-0">Total Sales:</span>
              <strong style={{ color: '#000' }}>{formatCurrency(closedDealsSummary.salesAmount)}</strong>
            </div>

            <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
              <CashStack size={15} className="text-primary" style={{ flexShrink: 0 }} />
              <span className="text-muted smallFont mb-0">Total Commission:</span>
              <strong style={{ color: '#000' }}>{formatCurrency(closedDealsSummary.commission)}</strong>
            </div>

            <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
              <GraphUpArrow size={15} className="text-success" style={{ flexShrink: 0 }} />
              <span className="text-muted smallFont mb-0">Conversion:</span>
              <strong style={{ color: '#000' }}>{closedDealsSummary.conversionRate}%</strong>
            </div>
          </div>
        </div>

        <div className="lead-report__table-body">
          {dealsLoading ? (
            <Loader />
          ) : (
            <>
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0 lead-report__table">
                  <thead>
                    <tr>
                      <th>Closed</th>
                      <th>Buyer</th>
                      <th>Store</th>
                      <th>Assigned To</th>
                      <th>Unit</th>
                      <th>Sales Price</th>
                      <th>Commission</th>
                      <th>Total GP</th>
                      <th>Date Funded</th>
                      <th>Time to Fund</th>
                      <th>Contract</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="text-center text-muted py-4">
                          No closed deals match your filters.
                        </td>
                      </tr>
                    ) : (
                      deals.map(deal => {
                        const dateFundedLabel = formatDateFunded(deal.additionalFields?.dateFunded);
                        const timeToFundDays = calcTimeToFundDays(
                          deal.additionalFields?.dateFunded,
                          deal.dateOfDeposit
                        );
                        return (
                          <tr key={deal._id}>
                            <td className="lead-report__date-cell">
                              <ClosedAtCell value={deal.closedAt} />
                            </td>
                            <td style={{ fontWeight: 700, color: '#000' }}>{deal.buyer || '—'}</td>
                            <td>{deal.salesLocation?.location || '—'}</td>
                            <td>{deal.contactOwner?.name || '—'}</td>
                            <td>{formatInventoryUnit(deal.inventoryUnit)}</td>
                            <td>{formatCurrencyDetail(deal.salesPrice)}</td>
                            <td>
                              <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.35rem' }}>
                                <span>{formatCurrencyDetail(deal.commissionDue)}</span>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="tinyFont py-0 px-1"
                                  onClick={() => setOpenDochubMeta({ contractId: deal._id })}
                                  title="Edit Commission Sheet"
                                >
                                  Edit
                                </Button>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.35rem' }}>
                                <span>{formatCurrencyDetail(deal.totalGP)}</span>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="tinyFont py-0 px-1"
                                  onClick={() => openPnlSidebar(deal)}
                                  title="Edit P&L / Total GP"
                                >
                                  Edit
                                </Button>
                              </div>
                            </td>
                            <td>{dateFundedLabel || '—'}</td>
                            <td>{timeToFundDays == null ? '—' : timeToFundDays}</td>
                            <td>
                              <ContractLinkCell contractId={deal._id} buyer={deal.buyer} />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
              {pagination.totalPages > 0 && (
                <div className="lead-report__pagination">
                  <span className="lead-report__pagination-info">
                    {pagination.total === 0
                      ? 'No results'
                      : `Showing ${(pagination.page - 1) * pagination.limit + 1}–${Math.min(
                          pagination.page * pagination.limit,
                          pagination.total
                        )} of ${pagination.total}`}
                  </span>

                  <div className="lead-report__pagination-controls">
                    <button
                      type="button"
                      className="lead-report__pagination-btn"
                      disabled={pagination.page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </button>
                    <span className="lead-report__pagination-page">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      type="button"
                      className="lead-report__pagination-btn"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </button>
                  </div>

                  <div className="lead-report__pagination-size">
                    <label htmlFor="salesPageSize" className="lead-report__toolbar-label">
                      Per page
                    </label>
                    <FilterSelect
                      id="salesPageSize"
                      ariaLabel="Per page"
                      value={limit}
                      options={PAGE_SIZE_OPTIONS.map(String)}
                      optionValues={PAGE_SIZE_OPTIONS}
                      onChange={value => {
                        setPage(1);
                        setLimit(Number(value));
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {!salesRepUser && (
        <div className="lead-report__table-card mb-3">
          <div
            className="d-flex align-items-end justify-content-between px-3 py-3 flex-wrap"
            style={{ gap: '0.75rem' }}
          >
            <h2 className="h6 mb-0 align-self-center">Grouped Summary</h2>
            <div className="d-flex align-items-end flex-wrap" style={{ gap: '0.65rem' }}>
              {showGroupLocationFilter && (
                <ToolbarFilter label="Sales Location" htmlFor="groupLocationFilter">
                  <FilterSelect
                    id="groupLocationFilter"
                    ariaLabel="Group sales location"
                    value={groupLocationFilter}
                    options={['All', ...(filterOptions.salesLocations || []).map(l => l.location)]}
                    optionValues={['All', ...(filterOptions.salesLocations || []).map(l => l._id)]}
                    onChange={setGroupLocationFilter}
                  />
                </ToolbarFilter>
              )}
              {adminUser && (
                <ToolbarFilter label="Group By" htmlFor="salesReportGroupBy">
                  <FilterSelect
                    id="salesReportGroupBy"
                    ariaLabel="Group by"
                    value={groupBy}
                    options={['Sales Rep', 'Sales Manager', 'Sales Location', 'Lender']}
                    optionValues={[
                      GROUP_BY.SALES_REP,
                      GROUP_BY.SALES_MANAGER,
                      GROUP_BY.SALES_LOCATION,
                      GROUP_BY.LENDER
                    ]}
                    onChange={value => {
                      setGroupBy(value);
                      if (value === GROUP_BY.SALES_LOCATION) {
                        setGroupLocationFilter('All');
                      }
                    }}
                  />
                </ToolbarFilter>
              )}
              <div className="d-flex align-items-center align-self-center flex-wrap" style={{ gap: '0.5rem' }}>
                <button
                  type="button"
                  className="lead-report__reset-btn"
                  onClick={() => printSalesReport(PRINT_SECTION.GROUPED_SUMMARY)}
                  disabled={!!printingSection || groupsLoading || groups.length === 0}
                >
                  <Printer size={14} />
                  {printingSection === PRINT_SECTION.GROUPED_SUMMARY ? 'Printing...' : 'Print'}
                </button>
                {!groupsLoading && summaryCsvData.length > 0 && (
                  <CSVLink
                    className="lead-report__reset-btn text-decoration-none"
                    data={summaryCsvData}
                    filename={`sales-report-summary-${moment().format('YYYY-MM-DD')}.csv`}
                  >
                    <FileEarmarkSpreadsheet size={14} className="text-success" />
                    Export summary
                  </CSVLink>
                )}
              </div>
            </div>
          </div>
          <div className="lead-report__table-body">
            {groupsLoading ? (
              <Loader />
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0 lead-report__table">
                  <thead>
                    <tr>
                      <th>{groupTableLabel}</th>
                      <th>Assigned</th>
                      <th>Closed</th>
                      <th>Sales Amount</th>
                      <th>Commission</th>
                      <th>Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          No data for the selected period.
                        </td>
                      </tr>
                    ) : (
                      groups.map(group => (
                        <tr key={group.id}>
                          <td style={{ fontWeight: 700, color: '#000' }}>{group.label}</td>
                          <td>{group.assigned}</td>
                          <td>{group.closed}</td>
                          <td>{formatCurrency(group.salesAmount)}</td>
                          <td>{formatCurrency(group.commission)}</td>
                          <td>
                            <span
                              className={`lead-report__conversion-badge lead-report__conversion-badge--${getConversionBadgeTone(
                                group.conversionRate
                              )}`}
                            >
                              {group.conversionRate}%
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}

      {pnlDeal && (
        <PnLSidebar
          contract={{ ...pnlDeal, newStatus: 'Contracted' }}
          onClose={() => setShowPnlSidebar(false)}
          onUpdate={handlePnlUpdate}
          appChoices={appChoices}
          visible={showPnlSidebar}
        />
      )}

      {openDochubMeta?.contractId && (
        <DochubSidebar
          show={openDochubMeta}
          onHide={() => setOpenDochubMeta(null)}
          appChoices={appChoices}
          source="Contract"
          contractId={openDochubMeta.contractId}
          document="Commission Sheet"
          onUpdateDocHub={handleCommissionSheetUpdate}
        />
      )}
    </div>
  );
};

export default SalesReport;
